/**
 * Crowd-noise detector.
 *
 * A goal is audible long before it is recognisable by any image model: the crowd
 * roars and the commentator's level jumps, and both last for seconds. Comparing
 * a short window against a slowly moving baseline catches that for the cost of a
 * square root per frame -- no GPU, no model, no training data.
 *
 * It fires on near misses, penalties and red cards too. That is not a defect
 * here: those are the other moments worth looking at.
 */

const FRAME_MS = 100;

/** Time constants, expressed as the window each average effectively covers. */
const BASELINE_SECONDS = 45;
const SHORT_SECONDS = 1.5;

/** Decibels above baseline that count as "something is happening". */
const TRIGGER_DB = 6;
/** How long it has to stay up, so a single shout does not count. */
const SUSTAIN_MS = 1200;
/** Silence after a trigger, so one goal does not fire ten times. */
const COOLDOWN_MS = 25_000;
/** Below this the input is effectively silent and the ratio is meaningless. */
const NOISE_FLOOR_DB = -60;

const toDb = (rms: number) => 20 * Math.log10(rms + 1e-9);

/** Smoothing factor for an exponential moving average over `seconds`. */
const alphaFor = (seconds: number) => 1 - Math.exp(-FRAME_MS / (seconds * 1000));

interface TrackState {
  baselineDb: number;
  shortDb: number;
  aboveSince: number | null;
  lastTrigger: number;
  primed: boolean;
}

export interface ExcitementEvent {
  id: string;
  /** Decibels above the track's own baseline. */
  deltaDb: number;
  at: number;
}

export class ExcitementDetector {
  #tracks = new Map<string, TrackState>();
  #timer: number | null = null;
  #levels: () => Map<string, number>;
  #onTrigger: (event: ExcitementEvent) => void;

  constructor(levels: () => Map<string, number>, onTrigger: (event: ExcitementEvent) => void) {
    this.#levels = levels;
    this.#onTrigger = onTrigger;
  }

  start(): void {
    if (this.#timer !== null) return;
    this.#timer = window.setInterval(() => this.pump(), FRAME_MS);
  }

  stop(): void {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
    this.#tracks.clear();
  }

  /** Current excursion above baseline per track, for display. */
  deltas(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [id, t] of this.#tracks) {
      out.set(id, t.primed ? t.shortDb - t.baselineDb : 0);
    }
    return out;
  }

  forget(id: string): void {
    this.#tracks.delete(id);
  }

  /**
   * Advances the detector by one frame. Public and clock-injectable so the
   * behaviour can be driven deterministically instead of waited out in real
   * time -- a 45-second baseline is not something to test with a stopwatch.
   */
  pump(now = Date.now()): void {
    const levels = this.#levels();

    for (const [id, rms] of levels) {
      const db = Math.max(NOISE_FLOOR_DB, toDb(rms));
      let track = this.#tracks.get(id);

      if (!track) {
        track = { baselineDb: db, shortDb: db, aboveSince: null, lastTrigger: 0, primed: false };
        this.#tracks.set(id, track);
        continue;
      }

      track.shortDb += (db - track.shortDb) * alphaFor(SHORT_SECONDS);

      // The baseline must not chase the very excursion it is meant to measure,
      // so it only tracks downward quickly and upward slowly.
      const rising = db > track.baselineDb;
      track.baselineDb += (db - track.baselineDb) * alphaFor(rising ? BASELINE_SECONDS : SHORT_SECONDS * 4);

      // A baseline needs a while before a comparison against it means anything.
      if (!track.primed && now - (track.lastTrigger || now) >= 0) {
        track.primed = this.#tracks.size > 0 && track.shortDb > NOISE_FLOOR_DB;
      }

      const delta = track.shortDb - track.baselineDb;

      if (delta < TRIGGER_DB) {
        track.aboveSince = null;
        continue;
      }

      track.aboveSince ??= now;
      const sustained = now - track.aboveSince >= SUSTAIN_MS;
      const cooled = now - track.lastTrigger >= COOLDOWN_MS;

      if (sustained && cooled && track.primed) {
        track.lastTrigger = now;
        track.aboveSince = null;
        this.#onTrigger({ id, deltaDb: delta, at: now });
      }
    }
  }
}
